import { useWeb3React } from "@web3-react/core";
import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import { utils, chains } from "@bkry/bowline-connectors";

/**
 * Custom hook that returns the connected account address from either Ethereum or Solana wallets
 * @returns {string|null} The connected account address or null if no wallet is connected
 */
const useAccount = () => {
  const {
    account: web3Account,
    provider: ethProvider,
    connector: ethConnector,
    chainId: ethChainId,
  } = useWeb3React();
  const wallet = useWallet();
  const { connection } = useConnection();
  const ethConnectorName = utils.getName(ethConnector);

  // Return Ethereum account or Solana publicKey (converted to string) or null
  const account =
    web3Account || (wallet?.publicKey ? wallet?.publicKey?.toBase58() : null);
  const solanaChainId = connection?.rpcEndpoint.includes("devnet")
    ? 1000000002
    : 1000000001;

  const ethNetworkChange = async (
    chainHex,
    chainId,
    chainOptions = {},
    callback = () => {}
  ) => {
    if (!ethConnector) return;
    try {
      if (ethConnectorName === "BowlineWallet" && ethConnector.changeNetwork) {
        await ethConnector.changeNetwork(chainHex);
        callback(true);
        return;
      } else {
        try {
          await connector?.provider.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: chainHex }],
          });
          callback(true);
          return;
        } catch (switchError) {
          if (switchError.code === networkChangeErrorCode) {
            const addChainParameters = chains.getAddChainParameters(chainId);
            const rpcUrls =
              addChainParameters?.rpcUrls?.length > 1
                ? addChainParameters?.rpcUrls.filter(
                    (rpcUrl) => !rpcUrl.includes("infura")
                  )
                : addChainParameters?.rpcUrls;
            try {
              await connector?.provider.request({
                method: "wallet_addEthereumChain",
                params: [
                  {
                    rpcUrls,
                    nativeCurrency: addChainParameters.nativeCurrency,
                    chainName: addChainParameters.chainName,
                    chainId: chainHex,
                    ...chainOptions,
                  },
                ],
              });
              callback(true);
            } catch (addError) {
              callback(false);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error switching Ethereum network:", error);
    }
  };

  return {
    account,
    isEth: !!web3Account,
    ethProvider,
    ethConnector,
    ethConnectorName,
    ethChainId,
    ethNetworkChange,
    isSol: !!wallet?.publicKey,
    solanaPublicKey: wallet?.publicKey,
    solanaConnection: connection,
    solanaWallet: wallet,
    solanaChainId,
  };
};

export default useAccount;
